import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useWelcomeOffer } from './WelcomeOfferContext';
import { apiClient } from '@/lib/api-client';
import { paymentStore } from '@/stores/paymentStore';

const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface WeeklyOfferContextType {
  showWeeklyOfferModal: boolean;
  setShowWeeklyOfferModal: (show: boolean) => void;
  currentWeeklyOffer: ScheduledOffer | null;
  currentWeeklyGroup: ScheduledOfferGroup | null;
  hasMoreWeeklyOffers: boolean;
  dismissCurrentOffer: () => void;
  checkAndShowWeeklyOffer: () => Promise<void>;
}

const WeeklyOfferContext = createContext<WeeklyOfferContextType | undefined>(undefined);

export const useWeeklyOffer = () => {
  const context = useContext(WeeklyOfferContext);
  if (context === undefined) {
    throw new Error('useWeeklyOffer must be used within a WeeklyOfferProvider');
  }
  return context;
};

const WEEKLY_OFFER_SHOWN_KEY = 'weekly_offer_shown_session';

export const WeeklyOfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow, hasHigherPriorityModal } = useModalQueue();
  const { showWelcomeOfferModal } = useWelcomeOffer();
  
  const [weeklyGroupsQueue, setWeeklyGroupsQueue] = useState<ScheduledOfferGroup[]>([]);
  const [weeklyOffersQueue, setWeeklyOffersQueue] = useState<ScheduledOffer[]>([]);
  
  const [currentWeeklyGroup, setCurrentWeeklyGroup] = useState<ScheduledOfferGroup | null>(null);
  const [currentWeeklyOffer, setCurrentWeeklyOffer] = useState<ScheduledOffer | null>(null);
  
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  
  const hasMoreWeeklyOffers = weeklyGroupsQueue.length > 0 || weeklyOffersQueue.length > 0;

  const loadNextOffer = useCallback(() => {
    if (weeklyGroupsQueue.length > 0) {
      const [nextGroup, ...remainingGroups] = weeklyGroupsQueue;
      setCurrentWeeklyGroup(nextGroup);
      setCurrentWeeklyOffer(null);
      setWeeklyGroupsQueue(remainingGroups);
      return true;
    }
    
    if (weeklyOffersQueue.length > 0) {
      const [nextOffer, ...remainingOffers] = weeklyOffersQueue;
      setCurrentWeeklyOffer(nextOffer);
      setCurrentWeeklyGroup(null);
      setWeeklyOffersQueue(remainingOffers);
      return true;
    }
    
    sessionStorage.setItem(WEEKLY_OFFER_SHOWN_KEY, 'true');
    setIsEligible(false);
    return false;
  }, [weeklyGroupsQueue, weeklyOffersQueue]);
  
  const dismissCurrentOffer = useCallback(() => {
    setIsTemporarilyHidden(false);
    setCurrentWeeklyGroup(null);
    setCurrentWeeklyOffer(null);
    notifyDismissed('WEEKLY_OFFER');
    
    setTimeout(() => {
      if (loadNextOffer()) {
        if (!paymentStore.getState().isOpen) {
          requestShow('WEEKLY_OFFER');
        }
      }
    }, 100);
  }, [notifyDismissed, loadNextOffer, requestShow]);
  
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      if (wasOpen && !isOpen && isEligible && (currentWeeklyOffer || currentWeeklyGroup)) {
        requestShow('WEEKLY_OFFER');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, currentWeeklyOffer, currentWeeklyGroup, requestShow]);

  const checkAndShowWeeklyOffer = useCallback(async () => {
    if (!user || !session) return;
    if (!user.email_verified) return;

    const hasShownThisSession = sessionStorage.getItem(WEEKLY_OFFER_SHOWN_KEY);
    if (hasShownThisSession) return;

    try {
      const data = await apiClient.get('/coins/packages');
      
      if (!data || (!data.scheduledOffers && !data.success)) return;

      const scheduledOffers = data.scheduledOffers || { groups: [], ungrouped: [] };
      
      const weeklyUngrouped = (scheduledOffers.ungrouped || []).filter(
        (offer: ScheduledOffer) => 
          offer.cadence_type === 'weekly' && 
          !offer.is_welcome_offer && 
          offer.can_purchase !== false && 
          (offer.purchases_remaining ?? 1) > 0
      );
      
      const weeklyGrouped = (scheduledOffers.groups || []).filter(
        (group: ScheduledOfferGroup) => {
          const hasWeeklyOffer = group.offers.some(
            (o: ScheduledOffer) => o.cadence_type === 'weekly' && !o.is_welcome_offer
          );
          const hasPurchasableOffer = group.offers.some(
            (o: ScheduledOffer) => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0
          );
          return hasWeeklyOffer && hasPurchasableOffer && !group.is_welcome_group;
        }
      );

      if (weeklyUngrouped.length > 0 || weeklyGrouped.length > 0) {
        setIsEligible(true);
        
        if (weeklyGrouped.length > 0) {
          const [firstGroup, ...remainingGroups] = weeklyGrouped;
          setCurrentWeeklyGroup(firstGroup);
          setCurrentWeeklyOffer(null);
          setWeeklyGroupsQueue(remainingGroups);
          setWeeklyOffersQueue(weeklyUngrouped);
        } else if (weeklyUngrouped.length > 0) {
          const [firstOffer, ...remainingOffers] = weeklyUngrouped;
          setCurrentWeeklyOffer(firstOffer);
          setCurrentWeeklyGroup(null);
          setWeeklyOffersQueue(remainingOffers);
        }
        
        if (!paymentStore.getState().isOpen && !hasHigherPriorityModal('WEEKLY_OFFER')) {
          requestShow('WEEKLY_OFFER');
        }
      }
    } catch {
      // Silently fail
    }
  }, [user, session, requestShow, hasHigherPriorityModal]);

  useEffect(() => {
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;
    
    if (user && session && user.email_verified && geoComplyPassed && !hasChecked && !showWelcomeOfferModal) {
      checkAndShowWeeklyOffer();
      setHasChecked(true);
    } else if (!user) {
      setHasChecked(false);
      setIsEligible(false);
      setWeeklyGroupsQueue([]);
      setWeeklyOffersQueue([]);
      setCurrentWeeklyGroup(null);
      setCurrentWeeklyOffer(null);
      if (sessionStorage.getItem(WEEKLY_OFFER_SHOWN_KEY)) {
        sessionStorage.removeItem(WEEKLY_OFFER_SHOWN_KEY);
      }
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, checkAndShowWeeklyOffer, showWelcomeOfferModal]);

  const queueShowsModal = shouldShow('WEEKLY_OFFER');
  const showWeeklyOfferModal = queueShowsModal && !isTemporarilyHidden;

  const setShowWeeklyOfferModal = useCallback((show: boolean) => {
    if (show) {
      setIsTemporarilyHidden(false);
    } else {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const value = {
    showWeeklyOfferModal,
    setShowWeeklyOfferModal,
    currentWeeklyOffer,
    currentWeeklyGroup,
    hasMoreWeeklyOffers,
    dismissCurrentOffer,
    checkAndShowWeeklyOffer,
  };

  return (
    <WeeklyOfferContext.Provider value={value}>
      {children}
    </WeeklyOfferContext.Provider>
  );
};
